import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadAPI } from '../services/api';
import { churchInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, 
  Mail, 
  User, 
  Heart, 
  MapPin, 
  Calendar, 
  BookOpen, 
  Users,
  Lock,
  Shield,
  AlertCircle,
  Camera,
  Loader2
} from 'lucide-react';

const CreateProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    spouseName: '',
    birthday: '',
    weddingAnniversary: '',
    favoriteScripture: '',
    privateProfile: false,
    avatar: ''
  });

  useEffect(() => {
    // Pre-fill with existing user data if available
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  // Redirect if profile is already complete
  useEffect(() => {
    if (user?.profileComplete) {
      navigate('/home');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    setValidationError('');
  };

  // Helper to construct proper avatar URL from relative path
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Handle Cloudinary URLs (new cloud storage format)
    if (avatar.includes('cloudinary.com') || avatar.includes('res.cloudinary.com')) {
      return avatar;
    }
    // Handle relative URLs (legacy local format)
    if (avatar.startsWith('/api/uploads/')) {
      return `${backendUrl}${avatar}`;
    }
    // Handle legacy absolute URLs
    if (avatar.includes('/api/uploads/')) {
      const uploadPath = avatar.substring(avatar.indexOf('/api/uploads/'));
      return `${backendUrl}${uploadPath}`;
    }
    return avatar.startsWith('http') ? avatar : `${backendUrl}${avatar}`;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Check file size - warn if over 10MB
    const MAX_SIZE_MB = 10;
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_SIZE_MB) {
      setError(`Photo is too large (${fileSizeMB.toFixed(1)}MB). Please select an image under ${MAX_SIZE_MB}MB.`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const result = await uploadAPI.uploadFile(file);
      // Store only the relative URL path for portability across environments
      const relativeUrl = result.url;
      setFormData({ ...formData, avatar: relativeUrl });
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload photo. Please try a smaller image or check your connection.');
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const hasName = formData.firstName.trim() && formData.lastName.trim();
    const hasPhone = formData.phone.trim();
    const hasAddress = formData.streetAddress.trim() && formData.city.trim() && formData.state.trim() && formData.zipCode.trim();
    const hasBirthday = formData.birthday.trim();

    if (!hasName) {
      setValidationError('Full name is required.');
      return false;
    }

    if (!hasPhone && !hasAddress && !hasBirthday) {
      setValidationError('Please provide at least one of the following: Phone Number, Complete Mailing Address, or Birth Date.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationError('');
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    // Combine address fields
    const mailingAddress = formData.streetAddress.trim() 
      ? `${formData.streetAddress}, ${formData.city}, ${formData.state} ${formData.zipCode}`.trim()
      : '';
    
    try {
      await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        mailingAddress,
        spouseName: formData.spouseName,
        birthday: formData.birthday,
        weddingAnniversary: formData.weddingAnniversary,
        favoriteScripture: formData.favoriteScripture,
        privateProfile: formData.privateProfile,
        avatar: formData.avatar,
        profileComplete: true
      });
      navigate('/home');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex flex-col items-center p-4 py-8">
      <div className="w-full max-w-md">
        {/* Church Logo */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-full bg-white p-1 shadow-lg mb-3">
            <img
              src={churchInfo.logo}
              alt={churchInfo.name}
              className="w-full h-full object-contain rounded-full"
            />
          </div>
          <div className="flex items-center gap-2 text-tan-400 mb-1">
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Welcome to the Family</span>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-navy-900 text-center mb-2">Create Your Profile</h2>
            <p className="text-sm text-gray-500 text-center mb-6">Help us get to know you better</p>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {validationError && (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{validationError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Profile Photo Section */}
              <div className="flex flex-col items-center mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <div 
                  className="relative cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {avatarPreview || formData.avatar ? (
                    <img 
                      src={avatarPreview || getAvatarUrl(formData.avatar)}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 border-tan-200"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-tan-200 flex items-center justify-center">
                      <User className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 w-8 h-8 bg-tan-500 rounded-full flex items-center justify-center border-2 border-white group-hover:bg-tan-600 transition-colors">
                    {uploading ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Tap to add profile photo</p>
              </div>

              {/* Full Name Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <User className="w-4 h-4 text-tan-400" />
                  <span className="font-semibold text-sm">Full Name *</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    name="firstName"
                    placeholder="First Name"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="lastName"
                    placeholder="Last Name"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Phone Number Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <Phone className="w-4 h-4 text-tan-400" />
                  <span className="font-semibold text-sm">Phone Number</span>
                </div>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="(559) 555-1234"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              {/* Email Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-tan-400" />
                    <span className="font-semibold text-sm">Email Address</span>
                  </div>
                  <span className="text-xs text-tan-400">(optional)</span>
                </div>
                <Input
                  name="email"
                  type="email"
                  placeholder="you@email.com"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Complete Mailing Address Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <MapPin className="w-4 h-4 text-tan-400" />
                  <span className="font-semibold text-sm">Complete Mailing Address</span>
                </div>
                <Input
                  name="streetAddress"
                  placeholder="Street Address"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.streetAddress}
                  onChange={handleChange}
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    name="city"
                    placeholder="City"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.city}
                    onChange={handleChange}
                  />
                  <Input
                    name="state"
                    placeholder="State"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.state}
                    onChange={handleChange}
                  />
                  <Input
                    name="zipCode"
                    placeholder="ZIP"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.zipCode}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Spouse Name Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-tan-400" />
                    <span className="font-semibold text-sm">Spouse Name</span>
                  </div>
                  <span className="text-xs text-tan-400">(if applicable)</span>
                </div>
                <Input
                  name="spouseName"
                  placeholder="Spouse's Full Name"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.spouseName}
                  onChange={handleChange}
                />
              </div>

              {/* Birth Date Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <Calendar className="w-4 h-4 text-tan-400" />
                  <span className="font-semibold text-sm">Birth Date</span>
                </div>
                <Input
                  name="birthday"
                  type="date"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.birthday}
                  onChange={handleChange}
                />
              </div>

              {/* Wedding Anniversary Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-tan-400" />
                    <span className="font-semibold text-sm">Wedding Anniversary</span>
                  </div>
                  <span className="text-xs text-tan-400">(if applicable)</span>
                </div>
                <Input
                  name="weddingAnniversary"
                  type="date"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.weddingAnniversary}
                  onChange={handleChange}
                />
              </div>

              {/* Favorite Bible Verse Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-navy-900 text-white px-3 py-2 rounded-lg">
                  <BookOpen className="w-4 h-4 text-tan-400" />
                  <span className="font-semibold text-sm">Favorite Bible Verse</span>
                </div>
                <textarea
                  name="favoriteScripture"
                  placeholder="Share your favorite scripture (e.g., John 3:16 - For God so loved the world...)"
                  className="w-full min-h-[80px] px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                  value={formData.favoriteScripture}
                  onChange={handleChange}
                />
              </div>

              {/* Privacy Setting */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privateProfile"
                    name="privateProfile"
                    checked={formData.privateProfile}
                    onChange={handleChange}
                    className="w-5 h-5 mt-0.5 text-tan-600 rounded focus:ring-tan-500"
                  />
                  <label htmlFor="privateProfile" className="text-sm text-gray-700">
                    <div className="flex items-center gap-2 font-medium text-navy-900 mb-1">
                      <Lock className="w-4 h-4" />
                      Keep my profile private
                    </div>
                    <span className="text-gray-500">Other members will only see your name and role in the directory</span>
                  </label>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-500 bg-white p-2 rounded-lg">
                  <Shield className="w-4 h-4 text-tan-600 flex-shrink-0 mt-0.5" />
                  <span>* Church leadership (Pastor, Admin, Deacons) can view complete profile details regardless of privacy settings.</span>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white font-semibold rounded-lg text-base"
                disabled={loading || uploading}
              >
                {loading ? 'Saving...' : 'Complete Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateProfilePage;
